import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CANDIDATE, ABOUT_GALLERY } from "@/data/content";
import { Marked, Asterisk, Star, ArrowDraw } from "@/components/campaign/HandDrawn";

const ABOUT_IMG = "/img/about-podium.webp";
const VOLUNTEER_IMG = "https://images.pexels.com/photos/6590932/pexels-photo-6590932.jpeg";

const About = () => {
  return (
    <main data-testid="about-page">
      <section className="section border-b border-ink/25">
        <div className="container-x grid gap-14 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> About the candidate
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl leading-[0.92] tracking-tight text-ink">
              A <Marked>mobilizer</Marked> who knows the doors of Hillsborough 40.
            </h1>
            <div className="mt-10 space-y-6 text-lg leading-relaxed text-ink/80">
              <p>
                Robert Richard-Snipes is a community mobilizer and activist based in Manchester,
                New Hampshire. He&apos;s a military brat originally from Virginia who put down deep
                roots in the Granite State during his time at the University of New Hampshire.
              </p>
              <p>
                Rob holds a bachelor&apos;s in Political Science and Women&apos;s and Gender Studies, and a master&apos;s
                degree in Community Development Policy and Practice from UNH&apos;s Carsey School of
                Public Policy. His academic work has always been rooted in community and paired with
                action — turning policy study into tangible systems that lift up the people of Manchester.
              </p>
              <p>
                Today he serves as board chair of <span className="font-bold">Black Lives Matter New Hampshire</span>, a board
                member of <span className="font-bold">SOS Recovery Community Organization</span>,
                and a board member of <span className="font-bold">Building Community in New Hampshire</span>. From civil rights to
                recovery and peer support to tenant power, Rob has spent years building coalitions
                that translate community needs into legislative wins.
              </p>
              <p>
                He&apos;s running for State Representative because{" "}
                <span className="italic font-semibold text-terracotta text-lg">too many decisions</span>{" "}
                in Concord are made without the people they affect ever stepping foot in the room.
                This campaign is about changing that.
              </p>
            </div>
          </div>
          <aside className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-terracotta -rotate-1" aria-hidden="true" />
              <img src={ABOUT_IMG} alt="Robert Richard-Snipes speaking at the Equity Leaders Fellowship" className="relative w-full h-[520px] object-cover object-center border-2 border-ink/85" />
            </div>
            <div className="mt-10 bg-white/5 border border-rule p-7" data-testid="about-quick-facts">
              <p className="overline inline-flex items-center gap-2">
                <Star className="h-4 w-4" /> At a glance
              </p>
              <ul className="mt-5 space-y-4 text-ink/85">
                <li><span className="font-bold uppercase tracking-wide">Home:</span> Manchester, NH</li>
                <li><span className="font-bold uppercase tracking-wide">Running in:</span> Hillsborough 40 — Manchester Wards 1, 3, 10, 11 &amp; 12</li>
                <li><span className="font-bold uppercase tracking-wide">From:</span> Virginia (military family)</li>
                <li><span className="font-bold uppercase tracking-wide">Education:</span> UNH — BA Political Science &amp; Women&apos;s and Gender Studies; MA Community Development Policy &amp; Practice (Carsey)</li>
                <li><span className="font-bold uppercase tracking-wide">Currently:</span> Board chair, BLM New Hampshire · Board, SOS Recovery Community Organization · Board, Building Community in NH</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-parchment section" data-testid="about-values-section">
        <div className="container-x grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-6">
            <div className="relative">
              <div className="absolute -inset-3 bg-forest rotate-1" aria-hidden="true" />
              <img src={VOLUNTEER_IMG} alt="Community volunteers organizing" className="relative w-full h-[420px] object-cover border-2 border-ink/85" />
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> What I believe
            </p>
            <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-6xl leading-[0.9] tracking-tight text-ink">
              Good policy starts where <Marked>people live</Marked>.
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-ink/80">
              Organizing taught me that the experts on a community are{" "}
              <span className="italic font-semibold text-terracotta text-base">the people inside it</span>.
              My job isn&apos;t to lecture neighbors — it&apos;s to fight for them in Concord and bring
              back honest answers.
            </p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <Link to="/issues" className="btn-secondary" data-testid="about-issues-btn">
                See the platform <ArrowUpRight size={18} />
              </Link>
              <Link to="/volunteer" className="btn-ghost" data-testid="about-volunteer-btn">
                Get involved
              </Link>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 text-ink/70">
              <span className="italic font-semibold text-terracotta text-sm">Don&apos;t wait —</span>
              <ArrowDraw className="h-5 w-12" />
            </div>
          </div>
        </div>
      </section>

      <section className="section" data-testid="about-gallery-section">
        <div className="container-x">
          <p className="overline inline-flex items-center gap-2">
            <Asterisk className="h-4 w-4" /> Off the campaign trail
          </p>
          <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-5xl leading-[0.95] tracking-tight text-ink">
            A few <Marked>moments</Marked> along the way.
          </h2>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT_GALLERY.map((g, idx) => (
              <figure
                key={g.src}
                className="bg-white/5 border border-rule rounded-2xl overflow-hidden group"
                data-testid={`about-gallery-${idx}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={g.src}
                    alt={g.caption}
                    loading="lazy"
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <figcaption className="p-4 text-sm text-ink/75 leading-snug italic">
                  {g.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
};

export default About;
